import { accentPrimary, fgContrast, overlayGradient, spacing, white } from '@/_vars'
import Button from '@/atoms/Button/Button'
import { animations } from '@/css-helpers/animated'
import { backgroundGray, brightRed, brightTurquoise, transparent, yellow } from '@/refactor/colors'
import { pixelFontSize } from '@/refactor/fonts'
import styled, { css, flex, muiUtils, padPx } from '@/styles'

import Box, { BoxTitle } from '../Box/Box'
import { IPropsModal, ModalVariant } from './Modal.types'

const MAX_WIDTH = 860

interface MaxWidth {
	maxWidth?: string | number
}

interface IProps extends IPropsModal {}

export const ModalRoot = styled.div<IProps>`
	${({ zIndex: $zIndex, open: $open }) => css`
		position: fixed;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		overflow: inherit;
		z-index: ${$zIndex};
		${flex({ justify: 'center', align: 'center' })}
		opacity: ${$open ? '1' : '0'};
		${!$open ? `pointer-events: none;` : ''}
	`}
`

export const ModalOverlay = styled.div<{ open: boolean }>`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 5;
	background-image: ${overlayGradient};
	animation: fade 600ms ease-in-out;

	${muiUtils.mobile} {
		max-height: 100vh;
	}

	${({ open: $open }) => ($open ? animations.fadeIn.animate(400) : '')}
`

// @TO-DO: find a better solution for edge cases BCDTDRCT-4161
export const ModalBase = styled.div<{ open: boolean } & MaxWidth>`
	width: 100%;
	max-width: ${({ maxWidth: $maxWidth }) => padPx($maxWidth || MAX_WIDTH)};
	position: relative;
	z-index: 10;
	background-color: white;
	border-radius: 30px;

	${(props) => (props.open ? animations.slideInTop.animate(400) : '')}

	margin: 0 auto;

	${muiUtils.mobile} {
		${flex({ direction: 'column' })}

		border-radius: 0;
		padding: 0;
		position: fixed;

		top: 0;
		bottom: 0;

		max-width: unset;
	}
`

export const ModalContent = styled.div<MaxWidth>`
	width: 100%;
	max-width: ${({ maxWidth: $maxWidth }) => padPx($maxWidth || MAX_WIDTH)};
	-webkit-overflow-scrolling: touch;
	touch-action: none;

	&:last-child {
		border-radius: 0 0 30px 30px;
	}

	${muiUtils.mobile} {
		overflow: hidden;
		max-width: unset;
		max-height: 100vh !important;
		${flex({ direction: 'column' })}
		flex: 1 1;
	}
`

const CompanyTypeModalColor: Record<ModalVariant, string> = {
	success: brightTurquoise,
	alert: brightRed,
	warning: yellow,
	waiting: transparent,
}

export const ModalControlRow = styled.div<{ variant?: ModalVariant; open: boolean } & MaxWidth>`
	${({ maxWidth: $maxWidth, variant: $variant, open: $open, theme }) => css`
		text-align: right;
		z-index: 300;
		pointer-events: none;
		max-width: ${padPx($maxWidth || MAX_WIDTH)};
		padding: ${theme.spacing(5, 6)};
		position: relative;
		background: ${$variant ? CompanyTypeModalColor[$variant] : transparent};
		border-radius: 30px 30px 0px 0px;
		color: white;
		display: flex;
		justify-content: space-between;

		${$open
			? `
		& > * {
			pointer-events: all;
		}
	`
			: ''}

		& .control-button {
			display: inline-block;
			background: #717790;
			color: ${fgContrast};
			text-align: center;
			cursor: pointer;
			transition: all 150ms ease-in-out;
			padding: ${theme.spacing(3, 5)};
			margin-left: ${theme.spacing(2)};

			&:hover {
				background: #7a809a;
			}
		}

		${muiUtils.mobile} {
			max-width: unset;
			border-radius: 0px;
			padding: ${spacing}px;
			margin-bottom: 0;
			height: 48px;

			background-color: ${$variant ? CompanyTypeModalColor[$variant] : accentPrimary};
		}
	`}
`

export const CloseButton = styled(Button)`
	padding: 0;
	line-height: unset;
	display: inline-block;
	text-decoration: none;
	vertical-align: middle;
	margin-left: 8px;
	${muiUtils.desktop} {
		padding: 9px;
		line-height: 0.8em;
	}
	${muiUtils.mobile} {
		min-height: max-content !important;
		padding: 0.25rem;
	}
`

export const MobileHeaderRow = styled.div`
	display: none;

	${muiUtils.mobile} {
		display: flex;
		${flex({ justify: 'space-between', align: 'center' })}
		color: ${white};
		font-size: ${pixelFontSize(20)};
		width: 100%;
	}
`

export const DesktopHeaderRow = styled.div`
	display: none;

	${muiUtils.desktop} {
		display: inline-block;
	}
`

export const BackColumn = styled.div``
export const TitleColumn = styled.div`
	font-size: ${pixelFontSize(14)};
	margin-top: 3px;
	${muiUtils.mobile} {
		margin-top: 0;
	}
`
export const DesktopTitle = styled(BoxTitle)`
	text-align: left;
	${muiUtils.mobile} {
		display: none;
	}
	font-weight: bold;
	align-self: center;
	margin-bottom: 0px;
	color: white;
`

export const BottomModalSeparator = styled.div`
	background: ${backgroundGray};
	height: 1px;

	margin-bottom: ${(p) => p.theme.spacing(6)};

	${muiUtils.mobile} {
		margin-bottom: ${(p) => p.theme.spacing(4)};
	}
`

export const BottomModalContainer = styled.div`
	display: flex;
	${flex({ direction: 'column', justify: 'space-between' })}
	padding: ${(p) => p.theme.spacing(6)};
	padding-top: 0;
	background: ${white};
	border-radius: 0px 0px 30px 30px;

	${muiUtils.mobile} {
		padding: 15px;
	}
`

export const BottomModalControls = styled.div`
	display: flex;
	flex-direction: row;
	width: 100%;
	gap: ${(p) => p.theme.spacing(2)};
	align-items: flex-end;

	${muiUtils.mobile} {
		flex-direction: column;
		align-items: stretch;
		gap: ${(p) => p.theme.spacing(2)};
	}
`

// @TO-DO: find a better solution for edge cases BCDTDRCT-4161
export const BottomModalButtonsContainer = styled.div`
	${flex({ direction: 'row', justify: 'flex-end' })}
	gap: ${(p) => p.theme.spacing(2)};
	margin-left: auto;
	& > *:not(:last-child) {
		margin-right: 0;
	}

	${muiUtils.mobile} {
		width: 100%;
		margin-left: 0;
		justify-content: center;
		flex-wrap: wrap;
	}
`

export const ModalBox = styled(Box).attrs(() => ({ padding: 30 }))<{
	variant: ModalVariant | undefined
	maxHeight?: string
}>`
	overflow-y: auto;
	min-height: 110px;
	-webkit-overflow-scrolling: touch !important;
	margin-bottom: 0;
	border-radius: 0;

	${muiUtils.mobile} {
		width: 100%;
		max-width: 100vw;
		&:last-child {
			border-radius: 0;
		}

		max-height: 100%;

		padding: ${(p) => p.theme.spacing(4)} ${(p) => p.theme.spacing(3)};
		flex: 1 1;
	}

	${muiUtils.desktop} {
		max-height: ${({ maxHeight: $maxHeight }) => ($maxHeight ? `${$maxHeight}` : 'calc(100vh - 400px)')};
		&:last-child {
			max-height: calc(100vh - 320px);
			border-radius: 0 0 30px 30px;
		}
	}

	padding-top: ${(p) => p.theme.spacing(8)};
	padding-bottom: ${(p) => p.theme.spacing(8)};
`

export const ButtonsContainer = styled.div`
	display: flex;
	gap: ${(p) => p.theme.spacing(2)};
	flex-wrap: wrap;

	width: 100%;

	& > button {
		flex: 1 0 calc(50% - ${(p) => p.theme.spacing(2)});
	}

	${muiUtils.desktop} {
		flex-wrap: nowrap;

		& > button {
			flex: 1 1 50%;
			padding-right: ${(p) => p.theme.spacing(5)};
			padding-left: ${(p) => p.theme.spacing(5)};
		}

		width: unset;
	}
`
